import { Component, OnInit, ViewChild } from '@angular/core';
import { LabeledInputComponent } from "../../../../shared/components/labeled-input/labeled-input.component";
import { LogoComponent } from "../../../../shared/components/logo/logo.component";
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { PrimaryButtonComponent } from "../../../../shared/components/primary-button/primary-button.component";
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../../../core/api/auth.service';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { AuthValidatorService } from '../../../../core/services/validators/auth-validator.service';
import { RecentUploadsService } from '../../../../core/services/recent-uploads.service';
import { LanguageService } from '../../../../core/services/language.service';

@Component({
  selector: 'app-signin',
  standalone: true,
  imports: [LabeledInputComponent, LogoComponent, PrimaryButtonComponent, TranslocoPipe, ToastComponent, RouterLink],
  templateUrl: './signin.component.html',
  styleUrl: './signin.component.css'
})
export class SigninComponent implements OnInit {
  // Email provided as a query parameter in the URL (e.g., /temp-password?email=email)
  email: string | null = null;

  // Email Inputs
  emailInput: string = '';
  emailInputError: string | null = null;
  invalidEmailErrorMessage: string = `Please enter a valid email address`;

  // Password Inputs
  passwordInput: string = '';
  passwordInputError: string | null = null;
  invalidPasswordErrorMessage: string = `Please enter a valid password`;

  signInClicked: boolean = false;

  @ViewChild(ToastComponent) toast!: ToastComponent;

  constructor(private route: ActivatedRoute, private router: Router, private translocoService: TranslocoService, private authService: AuthService, private authValidator: AuthValidatorService, private recentUploads: RecentUploadsService, private languageService: LanguageService) { }


  ngOnInit(): void {
    this.route.queryParamMap.subscribe(params => {
      this.email = params.get('email');
      if (this.email) {
        this.emailInput = this.email;
      }
    });

    // Populate with translation
    this.translocoService.selectTranslate('auth.invalid_email').subscribe((translation) => {
      this.invalidEmailErrorMessage = translation;
    });

    this.translocoService.selectTranslate('auth.invalid_password').subscribe((translation) => {
      this.invalidPasswordErrorMessage = translation;
    });
  }

  async signInClick() {
    // Prevent multiple clicks
    if (this.signInClicked) {
      return;
    }
    this.signInClicked = true;

    const passwordInputValue = this.passwordInput.trim();
    const emailInputValue = this.emailInput.trim();

    // Validate input
    let validationFailed: boolean = false;

    this.emailInputError = this.passwordInputError = '';

    const emailError = this.authValidator.validateEmail(emailInputValue);
    if (emailError) {
      this.emailInputError = emailError;
      validationFailed = true;
    }

    if (!passwordInputValue) {
      this.passwordInputError = this.translocoService.translate('shared.validators.password.required');
      validationFailed = true;
    }

    if (validationFailed) {
      this.signInClicked = false;
      return;
    }

    // Verify password
    let loginResult = undefined;
    try {
      loginResult = await firstValueFrom(this.authService.login(emailInputValue, passwordInputValue));
      this.recentUploads.clearUploads();
    }
    catch (error) {
      this.toast.showToast("shared.error", "shared.toast.credentials_error", 'error', 3000);
      this.passwordInputError = '';
      this.signInClicked = false;
      return;
    }

    this.languageService.setToUserLanguage();

    this.router.navigate(['/auth/mfa']);
    this.passwordInputError = '';
    this.signInClicked = false;
  }
}

